import { useMemo } from 'react';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { MINOR_IDENTITY_DOCUMENTS } from '@/constants/user';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectOperationData, selectOperationUser } from '@/redux/operation';

import {
  DocumentSection,
  DocumentStatus,
  RequiredDocumentSection,
} from '@/types/document';
import { User } from '@/types/user';

import { isButtonDisabled } from '@/utils/section';

import { MinorProofOfAddressDrawer } from '../drawers/MinorProofOfAddressDrawer/index';

const SECTION_TYPE = DocumentSection.ProofOfAddress;

type MinorProofOfAddressProps = {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export function MinorProofOfAddress({
  open,
  onOpen,
  onClose,
}: MinorProofOfAddressProps) {
  const { t } = useTranslate();
  const minorUser = useAppSelector(selectOperationUser);
  const { requiredDocumentSections } = useAppSelector(selectOperationData);

  useDrawerOpenTracker(open);

  const section = useMemo(() => {
    return requiredDocumentSections.find(
      section => section.type === SECTION_TYPE,
    );
  }, [requiredDocumentSections]);

  const isMissingIdentityDoc = useMemo(() => {
    const documents = minorUser?.documents || [];
    const missingDocuments = MINOR_IDENTITY_DOCUMENTS.filter(
      doc => !documents.some(d => d.type === doc),
    );

    return missingDocuments.length !== 0;
  }, [minorUser]);

  const isSharedWithAdult = minorUser?.addressDetailed?.sameAsAdult === true;
  const sectionStatus =
    getMinorAddressStatus(minorUser, section) || DocumentStatus.Pending;

  if (!minorUser || !section) {
    return null;
  }

  return (
    <>
      <DocumentActionItem
        title={t(`documentSection.${SECTION_TYPE}.title`)}
        richDescription={
          isSharedWithAdult ? t('minorPoA.status.sharedWithAdult') : undefined
        }
        description={isSharedWithAdult ? undefined : t('status.pending')}
        onClick={onOpen}
        status={sectionStatus}
        enabledForAllStatuses
        disabled={isMissingIdentityDoc && !minorUser?.adultConfirmKyc}
        disabledButton={
          isButtonDisabled(minorUser, section) || isSharedWithAdult
        }
      />
      <MinorProofOfAddressDrawer
        open={open}
        section={section}
        onClose={onClose}
      />
    </>
  );
}

export function getMinorAddressStatus(
  minorUser?: User | undefined,
  section?: RequiredDocumentSection,
) {
  const isSharedWithAdult = minorUser?.addressDetailed?.sameAsAdult === true;
  const sectionStatus = isSharedWithAdult
    ? DocumentStatus.Approved
    : section?.status;

  return sectionStatus;
}
